import type { DetailedDiff } from 'deep-object-diff';
import type {
    QuestionAndAnswerSmartApplyApplicationStateInput,
    ScreenerQuestionContextQuestion,
} from '../../../../__generated__/graphql-base-types';
import type { SavedScreenerAnswerFragment } from '../__generated__/QuestionsPage.generated';
import type { AnswerValue, ScreenerFileAnswer } from '../screenerQuestionUtils';
import { buildScreenerAnswers, getSavedAnswerValues } from '../screenerQuestionUtils';
import { ITV } from './ITV';
import { cleanFileAnswers, updateFalsePositiveAnswersInApplicationData } from './SQItvUtils';

/** Thrown from `persist` when SaveSQApplicationData returns no `payload.screener`. */
export const NO_SCREENER_PAYLOAD_MESSAGE = 'No screener payload returned from mutation';

export interface ScreenerItvApplicationData {
    questions: ScreenerQuestionContextQuestion[];
    answers: Record<string, AnswerValue>;
    locale: string;
    /** Question names the jobseeker changed this session — `buildScreenerAnswers`' `editedNames`. */
    editedNames: string[];
}

/**
 * Full round-trip ITV for the Auto Apply screener save. Forward uses `buildScreenerAnswers`;
 * backward uses `getSavedAnswerValues`; compare is a deep-diff of the flat answer state after
 * stripping known-harmless FILE drift and reconciling backend canonicalizations.
 */
export class ScreenerQuestionITV extends ITV<
    ScreenerItvApplicationData,
    QuestionAndAnswerSmartApplyApplicationStateInput[],
    readonly SavedScreenerAnswerFragment[]
> {
    protected normalizeApplicationData(
        clonedApplicationData: ScreenerItvApplicationData,
    ): ScreenerItvApplicationData {
        // Only the answers that will actually be sent — unsent FILE prefills / empty required
        // questions would otherwise show up as `deleted` against a payload that never included them.
        // FILE entries without a token are dropped here too: `buildScreenerAnswerInput` never
        // serializes them, so a name-only prefill sitting next to a tokened upload must not stay
        // on the baseline (that would false-fail against a payload of just the upload).
        const editedNames = new Set(clonedApplicationData.editedNames);
        const submitted = buildScreenerAnswers(
            clonedApplicationData.questions,
            clonedApplicationData.answers,
            clonedApplicationData.locale,
            editedNames,
        );
        const submittedIds = new Set(submitted.map((item) => item.questionId));
        const answers: Record<string, AnswerValue> = {};
        for (const question of clonedApplicationData.questions) {
            if (!submittedIds.has(question.id)) continue;
            const value = clonedApplicationData.answers[question.name] ?? [];
            answers[question.name] =
                question.type === 'FILE'
                    ? (value as ScreenerFileAnswer[]).filter((file) => Boolean(file.fileToken))
                    : value;
        }
        return { ...clonedApplicationData, answers };
    }

    protected forwardTransformation(
        applicationData: ScreenerItvApplicationData,
    ): QuestionAndAnswerSmartApplyApplicationStateInput[] {
        return buildScreenerAnswers(
            applicationData.questions,
            applicationData.answers,
            applicationData.locale,
            new Set(applicationData.editedNames),
        );
    }

    protected backwardTransformation(
        output: readonly SavedScreenerAnswerFragment[],
        applicationData: ScreenerItvApplicationData,
    ): ScreenerItvApplicationData {
        const questionsById = new Map(
            applicationData.questions.map((question) => [question.id, question]),
        );
        const answers: Record<string, AnswerValue> = {};
        for (const item of output) {
            const question = questionsById.get(item.questionId);
            if (!question) continue;
            // The payload can include the full draft screener (questions we omitted: unsent FILE
            // prefills, empty required). ITV only verifies what we submitted.
            if (!(question.name in applicationData.answers)) continue;
            answers[question.name] = getSavedAnswerValues(question, item.answer);
        }
        updateFalsePositiveAnswersInApplicationData(
            applicationData.questions,
            applicationData.answers,
            answers,
            applicationData.locale,
        );
        return { ...applicationData, answers };
    }

    protected cleanDiff(diff: DetailedDiff): void {
        [diff.added, diff.updated, diff.deleted].forEach((innerDiff) => {
            const record = innerDiff as Record<string, unknown>;
            delete record.questions;
            delete record.locale;
            delete record.editedNames;
            cleanFileAnswers(record);
        });
    }
}
